import { useCallback, useEffect, useRef } from 'react';

import { IconButton, IconButtonProps } from '@mui/material';
import { CloseIcon, MenuIcon } from 'icons';
import { appDrawerHide, appDrawerShow, appToggleMobileMenu, selectMobileMenuOpen } from 'modules/app/state';
import { selectPathname } from 'modules/router/state/router.selectors';
import { useAppDispatch, useAppSelector } from 'store';

interface MenuButtonProps extends IconButtonProps {
  open: boolean;
}

const MenuButton = ({ open, onClick, ...props }: MenuButtonProps) => (
  <IconButton
    sx={{ width: 32, height: 32 }}
    color="inherit"
    aria-label="Main menu"
    onClick={onClick}
    data-testid="menu-button"
    disableRipple
    {...props}
  >
    {open ? <CloseIcon fontSize="large" /> : <MenuIcon fontSize="large" />}
  </IconButton>
);

export const MobileMenuButton = () => {
  const isMobileMenuOpen = useAppSelector(selectMobileMenuOpen);
  const dispatch = useAppDispatch();
  const pathname = useAppSelector(selectPathname);

  // Picking a page closes the menu. `useToggleDrawer` does this too, but only inside layouts with a
  // sidebar; on a page without one (Account) the drawer vanished and this button stayed a close icon.
  // Two cases: a navigation that keeps this header mounted changes the pathname, and one to a page
  // with its own header unmounts this one.
  const lastPathname = useRef(pathname);
  useEffect(() => {
    if (lastPathname.current === pathname) return;
    lastPathname.current = pathname;
    dispatch(appToggleMobileMenu(false));
  }, [dispatch, pathname]);
  useEffect(() => () => void dispatch(appToggleMobileMenu(false)), [dispatch]);

  const handleNavigationToggle = useCallback(() => {
    dispatch(appToggleMobileMenu());
    if (isMobileMenuOpen) {
      dispatch(appDrawerHide());
    } else {
      dispatch(appDrawerShow());
    }
  }, [dispatch, isMobileMenuOpen]);

  return <MenuButton open={isMobileMenuOpen} onClick={handleNavigationToggle} />;
};
